import React, {
	useEffect,
	useState,
	useRef,
	useMemo,
	useCallback,
	useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import Socket from '../../connection/socket';
import { GreenroomState as GS } from '../../stores/types/types';
import type { GraphicInterface } from '../../stores/types/graphics';
import Dialog from '../general-components/dialog';
import * as VideoActions from '../../stores/actions/video';
import * as TabsActions from '../../stores/actions/tabs';
import {
	BACKSTAGE_COMMS_KEY_STATES,
	RESTRICTED_USER_STATES,
	RESTRICTED_CONNECTION_TYPES,
} from '../../stores/reducers/session';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import type {
	ChatMessage, ChatRoom,
} from '../../stores/types/chat';
import { EStatus } from '../../stores/types/chat';
import type { Shot, ShotFeed } from '../../stores/types/shots';
import * as keyListeners from '../../utils/keyboard-handler';
import Settings from './settings';
import ChatMain from '../chat/chat-main';
import Media from '../media/media';
import useShots from '../../contexts/shots/provider';
import InvitePresenterAlert from './invite-presenter-alert';
import usePrevious from '../../utils/use-previous';
import useUserActions from '../../hooks/use-user-actions';
import NotesTeleprompterWrapper from './notes-teleprompter-wrapper';
import OverlaysSettings from './overlay-settings';
import { ROOM_STATES } from '../../stores/reducers/settings';
import * as Storage from '../../utils/local-storage';
import printOTDebugLogForStreamId from '../../utils/opentok-debug-helper';
import AudioMixer from './audio-mixer';
import { AVContext } from '../../contexts/audio-video';
import { OpenTokContext } from '../../contexts/opentok/opentok-context';
import FeedsTab from './feeds-tab';
import * as ModalActions from '../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import RightDrawerOption from './right-drawer-option';
import ShotDrawer from './shot/shot-drawer';
import ButtonComms from './button-comms';
import AudienceDrawer from './audience/audience-drawer';
import InfoBadge, { InfoBadgeMsg } from '../general-components/tooltips/info-badge';
import useTabs from '../../hooks/use-tabs';
import { setCurrentGraphicsId } from '../../stores/actions/graphics';
import { setOpenContextMenuId, setSolutionCenterContentState } from '../../stores/actions/user-interface';
import GenerateContentTitle from './generate-content-title';
import Heading from '../general-components/v2/heading';
import { META_MENU_ROUTES, META_MENU_STATES, TABS } from '../../stores/types/tabs';
import { RoomStates } from '../../types/enums';
import AddFeedButton from './feeds/add-feed-button';
import MediaSettings from './media-settings';
import AudioSettings from './audio-settings';
import useMediaItemMemos from '../../hooks/use-media-memos';
import { SolutionCenterArticles, SolutionCenterContentTypes } from '../../types/solution-center';

interface Props {
	socket: Socket | null;
}

export default function RightPanelContainer(props: Props) {
	const dispatch = useDispatch();
	const { socket } = props;

	const {
		activeTab,
		metaMenuState,
		metaMenuActiveRoute,
		rightDrawerOpen,
	} = useSelector((s: GS) => s.TabsReducer);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const helpMenuOpen = useSelector((s: GS) => s.SessionReducer.helpMenuOpen);
	const avatarPopoverOpen = useSelector((s: GS) => s.SessionReducer.avatarPopoverOpen);
	const backstageCommsKeyState = useSelector((s: GS) => s.SessionReducer.backstageCommsKeyState);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const webrtcID = useSelector((s: GS) => s.SessionReducer.webrtcID);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const restrictedUserState = useSelector((s: GS) => s.SessionReducer.restrictedUserState);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const backstageCommsActive = useSelector((s: GS) => s.SettingsReducer.backstageCommsActive);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const connectedPresenterVideos = useSelector((s: GS) => s.VideoReducer.connectedPresenterVideos);
	const mainVideoStream = useSelector((s: GS) => s.VideoReducer.mainVideoStream);
	const sharingScreen = useSelector((s: GS) => s.VideoReducer.sharingScreen);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const shots = useSelector((s: GS) => s.ShotsReducer.shots);
	const lastUnseen = useSelector((s: GS) => s.ChatReducer.lastUnseen);
	const rooms = useSelector((s: GS) => s.ChatReducer.rooms);
	const directMessages = useSelector((s: GS) => s.ChatReducer.directMessages);
	const legacyModeratedComments = useSelector((s: GS) => s.ChatReducer.legacyModeratedComments);
	const moderatedEventsMessages = useSelector((s: GS) => s.ChatReducer.moderatedEventsMessages);
	const selectedRoom = useSelector((s: GS) => s.ChatReducer.selectedRoom);
	const mediaModalOpen = useSelector((s: GS) => s.MediaReducer.mediaModalOpen);
	const currentGraphicId = useSelector((s: GS) => s.GraphicsReducer.currentGraphicId);
	const graphicsArray = useSelector((s: GS) => s.GraphicsReducer.graphics);
	const modalOpen = useSelector((s: GS) => s.ModalReducer.modalOpen);
	const { debugOpentok } = useSelector((s: GS) => s.DebugReducer);
	const presenterAudienceTab = useSelector((s: GS) => s.SettingsReducer.presenterAudienceTab);
	const generatedContent = useSelector((s: GS) => s.GeneratedContentReducer.generatedContent);
	const {
		currentTemplate,
		currentTemplateMediaItem,
	} = useMediaItemMemos();
	const sessionInitialized = useSelector((s: GS) => s.OpenTokReducer.sessionInitialized);
	const isSynergySkySession = useSelector((s: GS) => s.SessionReducer.isSynergyskySession);

	const {
		audience_tab: audienceTab,
		use_whip_streams: useWhipStreams,
		use_title_library: useStylepacks,
	} = useSelector(
		(s: GS) => s.SettingsReducer.featureFlags,
	);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [unseenMessages, setUnseenMessages] = useState(0);
	const dialog = useRef<any>();
	const { isOnCanvas, currentShot } = useShots();
	const [backstageCommsBtnActive, setBackstageCommsBtnActive] = useState(false);
	const screenShareDialogRef = useRef<Dialog | null>(null);
	const [openScreenShareTab, setOpenScreenShareTab] = useState<boolean>(false);
	const [srtWarningVisible, setSrtWarningVisible] = useState<boolean>(false);
	const srtWarningRef = useRef(null);

	const previousTab = useSelector((s: GS) => s.TabsReducer.previousTab);

	const { getAV } = useContext(AVContext);
	const { audio, video } = getAV();

	const {
		derenderingTabRef,
		setDerenderTimeout,
		shouldRender,
	} = useTabs();

	const {
		toggleAudio,
		toggleVideo,
		toggleScreenShareDialog,
		backstageCommsToggle,
	} = useUserActions();

	const handleComms = (muted: boolean) => {
		backstageCommsToggle(muted);
		setBackstageCommsBtnActive(!muted);
	};

	const backstageCommsAllKeyUp = (e: any) => {
		if (e.keyCode === keyListeners.KEYS.KEY_SPACE && !isOnCanvas) {
			handleComms(true);
			window.removeEventListener('keyup', backstageCommsAllKeyUp);
		}
	};

	const backstageCommsAllKeyDown = () => {
		if (!isOnCanvas && !backstageCommsBtnActive) {
			handleComms(false);
			window.addEventListener('keyup', backstageCommsAllKeyUp);
		}
	};

	const closeTab = () => {
		dispatch(TabsActions.setRightDrawerOpen(false));
		dispatch(TabsActions.setActiveTab(TABS.NONE));
	};

	const applyTabChange = (tab: TABS) => {
		// do not close the tab if meta menu is open & new tab is already open
		// with the flag off, the drawer selection icons are not visible
		const blockClose = (
			metaMenuState === META_MENU_STATES.OPEN
			|| metaMenuState === META_MENU_STATES.OPENING
			|| metaMenuState === META_MENU_STATES.CLOSING
		);

		if (rightDrawerOpen && tab === activeTab && !blockClose) {
			closeTab();
		} else {
			if (!blockClose) {
				setDerenderTimeout(activeTab);
			}

			dispatch(TabsActions.setRightDrawerOpen(true));
			dispatch(TabsActions.setActiveTab(tab));

			if (tab !== TABS.OVERLAYS) {
				dispatch(setCurrentGraphicsId(0));
				dispatch(setOpenContextMenuId(null));
			}
		}

		dispatch(TabsActions.setMetaMenuState(META_MENU_STATES.CLOSING));
	};

	const tabHotkey = (down: boolean) => {
		if (
			mediaModalOpen
			|| avatarPopoverOpen
			|| helpMenuOpen
			|| metaMenuState === META_MENU_STATES.OPEN
			|| screenShareDialogRef.current?.state.open
		) { return; }

		const conditionalAudienceTab = audienceTab && roomState === ROOM_STATES.SHOWTIME
		&& (host || presenterAudienceTab)
			? [TABS.AUDIENCE]
			: [];

		const TAB_ORDER = host ? [
			TABS.FEEDS,
			TABS.SHOTS,
			TABS.MEDIA,
			TABS.NOTES_TELEPROMPTER,
			TABS.CHAT,
			...conditionalAudienceTab,
			currentGraphicId ? TABS.OVERLAYS : TABS.SETTINGS,
		] : [
			TABS.FEEDS,
			TABS.MEDIA,
			TABS.NOTES_TELEPROMPTER,
			TABS.CHAT,
			...conditionalAudienceTab,
		];

		if (!rightDrawerOpen) {
			const tabIndex = down ? 0 : TAB_ORDER.length - 1;
			applyTabChange(TAB_ORDER[tabIndex]);
		} else {
			let tabIndex = TAB_ORDER.findIndex((tabFromArr) => tabFromArr === activeTab);

			if (down) tabIndex += 1;
			else tabIndex -= 1;

			if (tabIndex < 0 || tabIndex > TAB_ORDER.length - 1) closeTab();
			else applyTabChange(TAB_ORDER[tabIndex]);
		}
	};

	function showScreenshareDialog() {
		if (!screenShareDialogRef.current
			|| RESTRICTED_CONNECTION_TYPES.includes(restrictedUserState)) return;
		if (isSynergySkySession) {
			dispatch(VideoActions.setSharingScreen(!sharingScreen));
		} else {
			screenShareDialogRef.current.confirm(
				'Enable screensharing?',
				'',
				'Yes',
				'No',
				(confirmed: boolean) => {
					if (confirmed) {
						dispatch(VideoActions.setSharingScreen(!sharingScreen));
					}
				},
			);
		}
		
	}

	const individualBscFeedRef = useRef(0);

	const individualCommsKeyUp = (e: KeyboardEvent) => {
		if (e.code === `Digit${individualBscFeedRef.current}` && !isOnCanvas) {
			individualBscFeedRef.current = 0;
			backstageCommsToggle(true);
			window.removeEventListener('keyup', individualCommsKeyUp);
		}
	};

	const bscIndividualKeyToggle = (e: KeyboardEvent) => {
		// to keep from firing constantly (problem with spacebar button too)
		if (individualBscFeedRef.current) return;

		if (modalOpen || embed) return;

		const strDigit = e.code.replace('Digit', '');
		const feedPosition = parseInt(strDigit, 10);

		if (Number.isNaN(feedPosition)) return;

		const userId = currentShot.shot_data.shotFeeds[feedPosition - 1]?.presenter;
		if (!userId) return;

		const targetedPresenterID = connectedUsers.find((u) => u.gr_user === userId)?.webrtcID;
		if (!targetedPresenterID) return;

		backstageCommsToggle(false, targetedPresenterID.toString());

		individualBscFeedRef.current = feedPosition;

		window.addEventListener('keyup', individualCommsKeyUp);
	};

	keyListeners.addListener(keyListeners.KEYS.KEY_1, bscIndividualKeyToggle, true);
	keyListeners.addListener(keyListeners.KEYS.KEY_2, bscIndividualKeyToggle, true);
	keyListeners.addListener(keyListeners.KEYS.KEY_3, bscIndividualKeyToggle, true);
	keyListeners.addListener(keyListeners.KEYS.KEY_4, bscIndividualKeyToggle, true);
	keyListeners.addListener(keyListeners.KEYS.KEY_5, bscIndividualKeyToggle, true);
	keyListeners.addListener(keyListeners.KEYS.KEY_6, bscIndividualKeyToggle, true);
	keyListeners.addListener(keyListeners.KEYS.KEY_7, bscIndividualKeyToggle, true);
	keyListeners.addListener(keyListeners.KEYS.KEY_8, bscIndividualKeyToggle, true);
	keyListeners.addListener(keyListeners.KEYS.KEY_9, bscIndividualKeyToggle, true);

	keyListeners.useKey(keyListeners.KEYS.KEY_A, () => !modalOpen && !embed && tabHotkey(false));
	keyListeners.useKey(keyListeners.KEYS.KEY_D, () => !modalOpen && !embed && tabHotkey(true));

	if (roomState !== ROOM_STATES.EDITING) {
		keyListeners.addListener(keyListeners.KEYS.KEY_SPACE, () => {
			if (
				!modalOpen
					&& host
					&& backstageCommsActive
					&& restrictedUserState === RESTRICTED_USER_STATES.NONE
					&& !isOnCanvas
			) {
				backstageCommsAllKeyDown();
			}
		});
	}

	const screenShareInShots = useMemo(() => {
		const currentIndex = shots.findIndex((shot: Shot) => shot.shot_id === currentShotId);
		return {
			selected: shots[currentIndex]?.shot_data.shotFeeds.some((feed: ShotFeed) => feed.screenshare === user?.email && feed.feedType === 'screenshare') ? currentShotId : null,
			next: shots[currentIndex + 1]?.shot_data.shotFeeds.some((feed: ShotFeed) => feed.screenshare === user?.email && feed.feedType === 'screenshare') ? currentShotId : null,
		};
	}, [currentShotId, shots, user]);

	const previousScreenShareInShots = usePrevious(screenShareInShots);

	useEffect(() => {
		// open the screenshare dialog if the user's screenshare is in the current or next shot
		// check the previous state to ensure the dialog doesnt show if it has already for the
		// current or next shot
		const newScreenShareInSelected = screenShareInShots.selected
			&& screenShareInShots.selected !== previousScreenShareInShots?.selected;
		const newScreenShareInNext = screenShareInShots.next
			&& screenShareInShots.next !== previousScreenShareInShots?.next;
		if (!screenShareDialogRef.current?.state.open
			&& !sharingScreen
			&& (newScreenShareInSelected || newScreenShareInNext)) {
			dispatch(TabsActions.setRightDrawerOpen(true));
			dispatch(TabsActions.setActiveTab(TABS.MEDIA));
			showScreenshareDialog();
			setOpenScreenShareTab(true);
		}
	}, [screenShareInShots, user]);

	useEffect(() => {
		if (!host) {
			dispatch(TabsActions.setRightDrawerOpen(true));
			dispatch(TabsActions.setActiveTab(TABS.FEEDS));
		}

		if (host && user && user?.tutorial_video_count < 5) {
			dispatch(ModalActions.setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.PRODUCT_TOUR,
			}));
		}
	}, [host, user?.tutorial_video_count]);

	useEffect(() => {
		if (debugOpentok) {
			window.debugOT = {
				printOTDebugLogForStreamId,
				connectedPresenterVideos,
				connectedUsers,
				subscribers: {},
				resubscribes: window?.debugOT?.resubscribes ?? {},
				subscribes: window?.debugOT?.subscribes ?? {},
				unsubscribes: window?.debugOT?.unsubscribes ?? {},
			};
		}
	}, [connectedPresenterVideos, connectedUsers, debugOpentok]);

	const getUnseen = useCallback((
		meetingId?: number,
		chatrooms?: ChatRoom[],
	): number => {
		if (meetingId && chatrooms && user?.gr_user) {
			const currentChatRoomsStatus: {
				[key: number]: number }
				| undefined = Storage.getStorageObject(
					`chat-rooms-last-seen-${meetingId}-${user.gr_user}`,
				);
			const unreadMessagesCount: number = chatrooms.reduce((acc: number, cur: ChatRoom) => {
				const {
					id,
					messages,
				} = cur;
				const lastSeenMessageId = currentChatRoomsStatus?.[id];
				const roomMessagesLength = messages?.length;

				if (lastSeenMessageId && roomMessagesLength) {
					return (
						acc
						+ messages.filter(
							(message) => !message.isOwnMessage && !message.seen,
						).length
					);
				}

				if (roomMessagesLength) {
					return acc + roomMessagesLength;
				}

				return acc;
			}, 0);

			return unreadMessagesCount;
		}

		return 0;
	}, []);

	useEffect(() => {
		const unseenRoomMessages = getUnseen(meeting?.meeting, rooms);

		const unseenDirectMessages = Object.values(directMessages).reduce(
			(unseenMessageSum, convo) => {
				const unseenCount = convo?.messages?.filter(
					(post) => !post.seen && !post.isOwnMessage,
				).length;

				return unseenMessageSum + unseenCount;
			},
			0,
		);

		const unseenModeratedEventsMessages = Array.from(
			moderatedEventsMessages,
			([, value]) => value,
		).reduce((unseenMessageSum, message) => (
			!message.seen && message.status !== EStatus.Deleted
				? unseenMessageSum + 1
				: unseenMessageSum
		), 0);

		const unseenModeratedLegacyComments = Object.values(legacyModeratedComments)
			.reduce((unseenMessageSum, message: ChatMessage) => (
				!message.seen && (!message.deleted && message.elevated)
					? unseenMessageSum + 1
					: unseenMessageSum
			), 0);

		setUnseenMessages(
			unseenRoomMessages
			+ unseenDirectMessages
			+ unseenModeratedLegacyComments
			+ unseenModeratedEventsMessages,
		);
	}, [
		directMessages,
		getUnseen,
		lastUnseen,
		legacyModeratedComments,
		meeting,
		moderatedEventsMessages,
		rooms,
		selectedRoom,
	]);

	const { initialSetup, forceFeedsTabRefresh, opentok } = useContext(OpenTokContext);

	const selectedGraphic = useMemo(() => graphicsArray.find(
		(graphic: GraphicInterface) => graphic.id === currentGraphicId,
	), [currentGraphicId, graphicsArray]);

	const selectedGraphicName = useMemo(
		() => selectedGraphic?.title ?? 'Design',
		[selectedGraphic, graphicsArray],
	);

	const selectedMediaName = useMemo(
		() => currentTemplateMediaItem?.custom_name ?? currentTemplateMediaItem?.original_name ?? 'Design',
		[currentTemplate, currentTemplateMediaItem],
	);

	const getGeneratedContentFromMediaItem = useMemo(
		() => generatedContent?.find((content) => content.fkId === currentTemplateMediaItem?.id),
		[currentTemplateMediaItem, generatedContent],
	);

	const getGeneratedContentFromGraphic = useMemo(
		() => generatedContent?.find((content) => content.fkId === currentGraphicId),
		[currentGraphicId, generatedContent],
	);

	useEffect(() => {
		// do not attempt to start until we are certain we have an opentok session id
		if (!forceFeedsTabRefresh && sessionInitialized) {
			initialSetup();
		}
	}, [
		forceFeedsTabRefresh,
		sessionInitialized,
	]);

	// Close drawer if going from showbuilder to live and there is an
	// overlay selected.
	useEffect(() => {
		if (roomState !== ROOM_STATES.EDITING
			&& (activeTab === TABS.OVERLAYS
				|| activeTab === TABS.GENERATE_MEDIA_CONTENT
				|| (activeTab === TABS.NONE && previousTab === TABS.GENERATE_MEDIA_CONTENT)
				|| (activeTab === TABS.NONE && previousTab === TABS.OVERLAYS)
			)
		) {
			closeTab();
		}
	}, [activeTab, previousTab, roomState]);

	// Close drawer if going from Showbuilder to Live mode and `Titles`
	// tab is open.
	useEffect(() => {
		switch (metaMenuActiveRoute) {
			case META_MENU_ROUTES.STYLE_PACKS:
			case META_MENU_ROUTES.STYLE_PACK_INFO:
				if (roomState !== ROOM_STATES.EDITING) {
					// if we are not in show builder, close this menu
					dispatch(TabsActions.closeMetaMenu());
				} else if (!rightDrawerOpen) {
					dispatch(TabsActions.setRightDrawerOpen(true));
				}
				break;

			case META_MENU_ROUTES.EVENT_SETTINGS:
			case META_MENU_ROUTES.SHARING:
				if (!rightDrawerOpen) {
					dispatch(TabsActions.setRightDrawerOpen(true));
				}
				break;
			default: // do nothing
				break;
		}
	}, [metaMenuActiveRoute, roomState]);

	// Close audience tab if it's open and one of the following occurs:
	// 1) Room state has changed from Live to Showbuilder
	// 2) User is a presenter and "Show audience tab to presenters" changes from on to off
	useEffect(() => {
		if (
			(roomState === ROOM_STATES.EDITING || (!presenterAudienceTab && !host))
			&& activeTab === TABS.AUDIENCE
		) {
			closeTab();
		}
	}, [activeTab, previousTab, roomState, presenterAudienceTab]);

	const commsActive = useMemo((): boolean => (
		backstageCommsBtnActive
		|| backstageCommsKeyState === BACKSTAGE_COMMS_KEY_STATES.ALL
	), [backstageCommsBtnActive, backstageCommsKeyState]);

	const commsDisabled = useMemo((): boolean => (
		restrictedUserState !== RESTRICTED_USER_STATES.NONE
		|| isOnCanvas
		|| !opentok?.session?.connection
	), [isOnCanvas, restrictedUserState, opentok?.session?.connection]);

	const [hasCam, hasMic] = useMemo(() => [
		!!mainVideoStream?.getVideoTracks().length,
		!!mainVideoStream?.getAudioTracks().length,
	], [mainVideoStream]);

	const hasSrtLocations = useMemo(
		() => onsiteLocations?.some((location) => (
			useWhipStreams ? location.inputType === 'srt' : location.useSrt
		)),
		[onsiteLocations, useWhipStreams],
	);
	const prevHasSrtLocations = usePrevious(hasSrtLocations);

	useEffect(() => {
		if (hasSrtLocations && !prevHasSrtLocations) {
			setSrtWarningVisible(true);
		} else if (!hasSrtLocations) {
			setSrtWarningVisible(false);
		}
	}, [hasSrtLocations, prevHasSrtLocations]);

	return (<>
		<div className={classNames('right-drawer-wrapper')}>
			{embed ? <></> : (
				<div className={classNames(
					'right-drawer-content',
					{
						open: rightDrawerOpen,
						feeds: activeTab === TABS.FEEDS,
						shots: activeTab === TABS.SHOTS,
					},
				)}>

				{/* Drawer: Feeds */}
				{shouldRender.feeds && (<div className={classNames('tab overflow-y-scroll', {
					active: activeTab === TABS.FEEDS,
					wasActive: derenderingTabRef.current === TABS.FEEDS,
				})} key='feeds'>
					<Heading title='Feeds' classNamesExtra='margin-right-small'/>
					{roomState !== ROOM_STATES.EDITING && (
						<div id='canvas-volume-mixer' className='tab-row' style={{ paddingLeft: '24px' }}>
							<AudioMixer />
						</div>
					)}
					{!forceFeedsTabRefresh && (
						<FeedsTab
							socket={socket}
							className='tab-row'
						/>
					)}
					<>
						<div className={classNames('position-fixed position-bottom controls-gradient', srtWarningVisible && 'srt-gradient')} />
						<div className='flex-column flex-justify-flex-end position-fixed position-bottom volume-controls'>
							<CSSTransition
								in={srtWarningVisible}
								timeout={250}
								classNames="fade-in-out"
								unmountOnExit
								nodeRef={srtWarningRef}
							>
								<div ref={srtWarningRef} className='srt-warning-container'>
									<div className='srt-warning'>
										<div className='srt-warning-header'>
											<div className="flex-row flex-align-center gap-extra-small">
												<Icon name={ICONS.TIMER} color={COLORS.WHITE} size={16} />
												<span className="bl-font-s1">SRT has additional latency</span>
											</div>
											<div className="flex-row flex-align-center gap-small">
												<button
													className="srt-learn-more"
													onClick={() => {
														dispatch(setSolutionCenterContentState({
															id: SolutionCenterArticles.FeatureOverviewSRTConnection,
															type: SolutionCenterContentTypes.Article,
														}));
													}}
												>
													<span
														className="bl-font-l1 text-green"
														style={{ display: 'contents' }}
													>
														Learn More
													</span>
												</button>
												<button
													className="button-close transparent text-hightlight"
													onClick={() => setSrtWarningVisible(false)}
												>
													<div className="flex-center">
														<Icon name={ICONS.CLOSE} color={COLORS.TEXT_GRAY_400} size={16} />
													</div>
												</button>
											</div>
										</div>
										<div className="srt-warning-content">
											<span className="bl-font-p2">You’ll incur more latency than the rest of your feeds</span>
										</div>
									</div>
								</div>
							</CSSTransition>
							<div className='align-items-center tab-row justify-content-space-between gap-small background-medium'>
								{webrtcID && (
									<>
										{hasCam && (
												<button
													className={classNames(
														'padding-medium round',
														video ? 'background-lighter' : 'background-red local-bttn-critical-default',
													)}
													onClick={(e) => {
														toggleVideo();
														e.currentTarget.blur();
													}}>
												<Icon
													name={video ? ICONS.CAMERA_ON : ICONS.CAMERA_OFF}
													color={COLORS.WHITE}
													size={18}
												/>
											</button>
										)}
										{hasMic && (
												<button
													className={classNames(
														'padding-medium round',
														audio ? 'background-lighter' : 'background-red local-bttn-critical-default',
													)}
													onClick={(e) => {
														toggleAudio();
														e.currentTarget.blur();
													}}>
												<Icon
													name={audio ? ICONS.MICROPHONE_ON : ICONS.MICROPHONE_OFF}
													color={COLORS.WHITE}
													size={18}
												/>
											</button>
										)}
									</>
								)}
								{host && (
									<>
										{
											roomState === RoomStates.showbuilder && (
												<AddFeedButton />
											)
										}
										{roomState === ROOM_STATES.SHOWTIME ? (
											<ButtonComms
												active={commsActive}
												disabled={commsDisabled}
												featureToggleOn={backstageCommsActive}
												onMouseDown={() => handleComms(false)}
												onMouseLeave={() => handleComms(true)}
												onMouseUp={() => handleComms(true)}
												additionalClasses={[
													'presenters-button',
													'padding-medium',
												]}
											/>
										) : (
											<></>
										)}
									</>
								)}
								{!host && (
									<button onClick={toggleScreenShareDialog} className={classNames('width-full', 'padding-medium', 'background-lighter', 'round', 'text-white', { sharing: sharingScreen, disabled: RESTRICTED_CONNECTION_TYPES.includes(restrictedUserState) || !webrtcID })}>
										<div className='button-content align-items-center'>
											<Icon className='margin-right-small' name={sharingScreen ? ICONS.SQUARE : ICONS.MEDIA_SHARE} color={sharingScreen ? COLORS.WHITE : COLORS.DEFAULT_GRAY} size={sharingScreen ? 10 : 18} />
											<span className='text-transform-capitalize font-weight-heavy white-space-nowrap font-weight-heavy'>{sharingScreen ? 'stop sharing' : 'share'} screen</span>
										</div>

									</button>
								)}
							</div>
						</div>
					</>
				</div>
				)}

				{/* Drawer: Shot */}
				{shouldRender.shots ? (
					<ShotDrawer
						active={activeTab === TABS.SHOTS}
						wasActive={derenderingTabRef.current === TABS.SHOTS}
						closeTab={closeTab}
						socket={socket}
						commsActive={commsActive}
						commsDisabled={commsDisabled}
						backstageCommsToggle={backstageCommsToggle}
						key={'shots'}
					/>
				) : <></>}

				{/* Drawer: Media */}
				{shouldRender.media && (
					<div key='media' id="media-tab" className={classNames('tab', 'no-overflow-y', {
						active: activeTab === TABS.MEDIA,
						noscroll: mediaModalOpen,
						wasActive: derenderingTabRef.current === TABS.MEDIA,
					})}>
						<Heading
							title='Media'
							badge={<InfoBadge below message={InfoBadgeMsg.MediaUpload} />}
							alignBadge='left'
						/>
						<Media
							isForShotModal={false}
							socket={socket}
							openScreenShareTab={openScreenShareTab}
							onScreenSharePrompt={() => setOpenScreenShareTab(false)}
						/>
					</div>
				)}

				{/* Drawer: Notes */}
				{shouldRender.notesTeleprompter && (
					<div key='tele' className={classNames('tab', {
						active: activeTab === TABS.NOTES_TELEPROMPTER,
						wasActive: derenderingTabRef.current === TABS.NOTES_TELEPROMPTER,
					})}>
						<Heading title='Notes' />
						{activeTab === TABS.NOTES_TELEPROMPTER
							&& <NotesTeleprompterWrapper
								socket={socket}
								isForAllNotes={false}
							/>
						}
					</div>
				)}

				{/* Drawer: Chat */}
				{shouldRender.chat
					&& <div key='chat' className={classNames('tab', {
						active: activeTab === TABS.CHAT,
						wasActive: derenderingTabRef.current === TABS.CHAT,
					})}>
						<Heading title='Chat' />
						<ChatMain socket={socket} visible={activeTab === TABS.CHAT} />
					</div>
				}

				{/* Drawer: Design */}
				{shouldRender.settings && (
					<div key='settings' className={classNames('tab', {
						active: activeTab === TABS.SETTINGS,
						wasActive: derenderingTabRef.current === TABS.SETTINGS,
					})}>
						<Heading title='Design' />
						{activeTab === TABS.SETTINGS && <Settings socket={socket} />}
					</div>
				)}

				{shouldRender.audience && audienceTab && (
					<div key='aud' className={classNames('tab', {
						active: activeTab === TABS.AUDIENCE,
						wasActive: derenderingTabRef.current === TABS.AUDIENCE,
					})}>
						<Heading title='Audience' />
						<AudienceDrawer />
					</div>
				)}

				{/* A portal inserts the shot notes editor toolbar
				here (see teleprompter-section-editor.tsx) */}
				{/* Drawer: Shot Notes */}
				<div id="teleprompter-section-editor-controls" />

				{/* Drawer: Animation (overlay selected) */}
				{shouldRender.overlays
					&& roomState === ROOM_STATES.EDITING
					&& derenderingTabRef.current !== TABS.OVERLAYS
					&& (
						<div key='overlay' className={classNames('tab', {
							active: activeTab === TABS.OVERLAYS,
							noscroll: mediaModalOpen,
							wasActive: derenderingTabRef.current === TABS.OVERLAYS,
							'stylepack-edit-tab': useStylepacks
						})}>
							<Heading
								title={
									<GenerateContentTitle
										key={selectedGraphic?.id}
										generatedContent={getGeneratedContentFromGraphic}
										graphic={selectedGraphic}
										title={selectedGraphicName}
									/>
								}
							/>
							<OverlaysSettings />
						</div>
					)}

				{/* drawer: media generated content drawer */}
				{roomState === ROOM_STATES.EDITING
					&& shouldRender.generateMediaContent
					&& currentTemplate
					&& currentTemplateMediaItem
					&& (<div
							key='media-content'
							className={classNames('tab stylepack-edit-tab', {
								active: activeTab === TABS.GENERATE_MEDIA_CONTENT,
								wasActive: derenderingTabRef.current === TABS.GENERATE_MEDIA_CONTENT,
								noscroll: true,
							})}>
							<Heading
								title={
									<GenerateContentTitle
										key={selectedGraphic?.id}
										generatedContent={getGeneratedContentFromMediaItem}
										mediaItem={currentTemplateMediaItem}
										title={selectedMediaName}
									/>
								}
							/>
							<MediaSettings />
						</div>
					)}

				{/* Drawer: Audio Settings */}
				{shouldRender.audioSettings && (
					<>
						<Heading title='Media' />
						<AudioSettings />
					</>
				)}
				</div>
			)}
			<div className={classNames('right-drawer-menu')}>
				<RightDrawerOption
					name="Feeds"
					icon={ICONS.TAB_FEEDS}
					isActive={activeTab === TABS.FEEDS}
					onClick={() => applyTabChange(TABS.FEEDS)}
				/>
				{host && (
					<RightDrawerOption
						name="Shot"
						icon={ICONS.PERSON_IN_SHOT}
						isActive={activeTab === TABS.SHOTS}
						onClick={() => applyTabChange(TABS.SHOTS)}
					/>
				)}
				<RightDrawerOption
					name="Media"
					icon={ICONS.TAB_MEDIA}
					isActive={activeTab === TABS.MEDIA}
					onClick={() => applyTabChange(TABS.MEDIA)}
				/>
				<RightDrawerOption
					name="Notes"
					icon={ICONS.TAB_NOTES}
					isActive={activeTab === TABS.NOTES_TELEPROMPTER}
					onClick={() => applyTabChange(TABS.NOTES_TELEPROMPTER)}
				/>
				<RightDrawerOption
					name="Chat"
					icon={ICONS.TAB_CHAT}
					isActive={activeTab === TABS.CHAT}
					onClick={() => applyTabChange(TABS.CHAT)}
					unreadNotifications={unseenMessages}
				/>
				{audienceTab && roomState === ROOM_STATES.SHOWTIME && (host || presenterAudienceTab) && (
					<RightDrawerOption
						name="Audience"
						icon={ICONS.PEOPLE}
						isActive={activeTab === TABS.AUDIENCE}
						onClick={() => applyTabChange(TABS.AUDIENCE)}
						customSize={20}
					/>
				)}
				{host && (
					<RightDrawerOption
						name="Design"
						icon={ICONS.DESIGN}
						isActive={activeTab === TABS.SETTINGS}
						onClick={() => applyTabChange(TABS.SETTINGS)}
					/>
				)}
			</div>
		</div>
		<Dialog
			right={true}
			ref={dialog}
		/>
		<Dialog
			ref={screenShareDialogRef}
			right={true}
			rightXPixel={108}
			yPixel={324}
			width={376}
		/>
		{host && <InvitePresenterAlert socket={socket} />}
		<div id="add-feed-portal" />
	</>);
}
